import { FormSection } from '@@/form-components/FormSection';
import { SwitchField } from '@@/form-components/SwitchField';

import { DockerSecurityFormData } from './types';

interface Props {
  formData: DockerSecurityFormData;
  onChange: <K extends keyof DockerSecurityFormData>(
    key: K,
    value: DockerSecurityFormData[K]
  ) => void;
}

/**
 * Docker Security Policy form fields.
 * Pure field renderer without FormActions - used by both create and edit modes.
 */
export function DockerSecurityFormFields({ formData, onChange }: Props) {
  return (
    <FormSection title="Docker security settings">
      <SwitchField
        checked={!formData.allowBindMountsForRegularUsers}
        onChange={(value) => onChange('allowBindMountsForRegularUsers', !value)}
        label="Hide bind mounts for non-administrators"
        tooltip="When enabled, regular users will not be able to use bind mounts when creating containers."
        labelClass="col-sm-7 col-lg-4"
        fieldClass="!mb-4"
        data-cy="disable-bind-mounts-switch"
      />
      <SwitchField
        checked={!formData.allowPrivilegedModeForRegularUsers}
        onChange={(value) =>
          onChange('allowPrivilegedModeForRegularUsers', !value)
        }
        label="Hide privileged mode for non-administrators"
        tooltip="When enabled, regular users will not be able to use privileged mode when creating containers."
        labelClass="col-sm-7 col-lg-4"
        fieldClass="!mb-4"
        data-cy="disable-privileged-mode-switch"
      />
      <SwitchField
        checked={!formData.allowHostNamespaceForRegularUsers}
        onChange={(value) =>
          onChange('allowHostNamespaceForRegularUsers', !value)
        }
        label="Hide the use of host PID 1 for non-administrators"
        tooltip="Prevent users from accessing the host filesystem through the host PID namespace."
        labelClass="col-sm-7 col-lg-4"
        fieldClass="!mb-4"
        data-cy="disable-host-namespace-switch"
      />
      <SwitchField
        checked={!formData.allowStackManagementForRegularUsers}
        onChange={(value) =>
          onChange('allowStackManagementForRegularUsers', !value)
        }
        label="Hide the use of Stacks for non-administrators"
        labelClass="col-sm-7 col-lg-4"
        fieldClass="!mb-4"
        data-cy="disable-stack-management-switch"
      />
      <SwitchField
        checked={!formData.allowDeviceMappingForRegularUsers}
        onChange={(value) =>
          onChange('allowDeviceMappingForRegularUsers', !value)
        }
        label="Hide device mappings for non-administrators"
        labelClass="col-sm-7 col-lg-4"
        fieldClass="!mb-4"
        data-cy="disable-device-mapping-switch"
      />
      <SwitchField
        checked={!formData.allowContainerCapabilitiesForRegularUsers}
        onChange={(value) =>
          onChange('allowContainerCapabilitiesForRegularUsers', !value)
        }
        label="Hide container capabilities for non-administrators"
        labelClass="col-sm-7 col-lg-4"
        fieldClass="!mb-4"
        data-cy="disable-container-capabilities-switch"
      />
      <SwitchField
        checked={!formData.allowSysctlSettingForRegularUsers}
        onChange={(value) =>
          onChange('allowSysctlSettingForRegularUsers', !value)
        }
        label="Hide sysctl settings for non-administrators"
        labelClass="col-sm-7 col-lg-4"
        fieldClass="!mb-4"
        data-cy="disable-sysctl-setting-switch"
      />
    </FormSection>
  );
}
